import {
  ActionIcon,
  Flex,
  Select,
  NumberInput,
  Text,
  Group,
  Divider,
  Container,
} from '@mantine/core';
import { IconTrash, IconCurrencyShekel } from '@tabler/icons-react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import classes from './style.module.css';
import { calculateInterestAndLinkage, calculatePayments } from '@/pages/CustomizedMix/functions';
import { formatNumberWithCommas } from '@/helpers/string';

const CustomizedMixTrancheDetails = (props) => {
  const {
    handleDelete,
    trancheOptions,
    loanGoalOptions,
    periodOptions,
    form,
    mixIndex,
    trancheIndex,
    tranchesData,
  } = props;

  const { t } = useTranslation();

  const { mixes } = form.values;
  const mix = mixes[mixIndex];
  const tranche = mix.tranches[trancheIndex];

  const calculatePaymentsObj = {
    amount: tranche.amount,
    interestRate: tranche.interest,
    periodMonths: tranche.period,
    linkage: tranche.linkage,
  };

  const { monthlyPayment, totalRepayment } = calculatePayments(calculatePaymentsObj);

  const handleTrancheChange = (value) => {
    const currentTranche = {
      ...form.values.mixes[mixIndex].tranches[trancheIndex],
      tranche: value,
    };

    const calc = calculateInterestAndLinkage(
      currentTranche.tranche,
      currentTranche.goal,
      tranchesData,
      currentTranche.period,
    );

    const updatedTranche = {
      ...currentTranche,
      interest: calc.interest,
      linkage: calc.linkage,
    };

    const newTranches = [...form.values.mixes[mixIndex].tranches];
    newTranches[trancheIndex] = updatedTranche;

    const newMixes = [...form.values.mixes];
    newMixes[mixIndex] = {
      ...newMixes[mixIndex],
      tranches: newTranches,
    };

    form.setFieldValue('mixes', newMixes);
  };

  const handleGoalChange = (value) => {
    const newTranche = {
      ...form.values.mixes[mixIndex].tranches[trancheIndex],
      goal: value,
    };

    const calc = calculateInterestAndLinkage(
      newTranche.tranche,
      newTranche.goal,
      tranchesData,
      newTranche.period,
    );

    const updatedTranche = {
      ...newTranche,
      interest: calc.interest,
      linkage: calc.linkage,
    };

    const newTranches = [...form.values.mixes[mixIndex].tranches];
    newTranches[trancheIndex] = updatedTranche;

    const newMixes = [...form.values.mixes];
    newMixes[mixIndex] = {
      ...newMixes[mixIndex],
      tranches: newTranches,
    };

    form.setFieldValue('mixes', newMixes);
  };

  const handleAmountChange = (value) => {
    const newTranche = { ...tranche, amount: value };

    const newTranches = [...form.values.mixes[mixIndex].tranches];
    newTranches[trancheIndex] = newTranche;

    const newMixes = [...form.values.mixes];
    newMixes[mixIndex] = { ...newMixes[mixIndex], tranches: newTranches };

    form.setFieldValue('mixes', newMixes);
  };

  const handlePeriodChange = (value) => {
    const calc = calculateInterestAndLinkage(
      form.values.mixes[mixIndex].tranches[trancheIndex].tranche,
      form.values.mixes[mixIndex].tranches[trancheIndex].goal,
      tranchesData,
      value,
    );

    const newTranche = {
      ...tranche, period: value, interest: calc.interest, linkage: calc.linkage,
    };
    const newTranches = [...form.values.mixes[mixIndex].tranches];
    newTranches[trancheIndex] = newTranche;

    const newMixes = [...form.values.mixes];
    newMixes[mixIndex] = { ...newMixes[mixIndex], tranches: newTranches };

    form.setFieldValue('mixes', newMixes);
  };

  return (
    <Container p={0} m={0} className={classes.container}>
      <Flex
        bg='#F8FAFC'
        p='md'
        gap='xl'
        align='flex-start'
        direction='row'
      >
        <NumberInput
            label= {t('components.CustomizedMixTrancheDetails.amount.label')}
            thousandSeparator=','
            leftSection={<IconCurrencyShekel size='1rem' color='gray' />}
            value={tranche.amount || ''}
            error={form.errors.mixes?.[mixIndex]?.tranches?.[trancheIndex]?.amount}
            onChange={handleAmountChange}
        />

        <Select
            label={ t('components.CustomizedMixTrancheDetails.tranche.label')}
            value={tranche.tranche || null}
            data={trancheOptions}
            error={form.errors.mixes?.[mixIndex]?.tranches?.[trancheIndex]?.tranche}
            onChange={handleTrancheChange}
        />

        <Select
            label={t('components.CustomizedMixTrancheDetails.goal.label')}
            value={tranche.goal || null}
            data={loanGoalOptions}
            error={form.errors.mixes?.[mixIndex]?.tranches?.[trancheIndex]?.goal}
            onChange={handleGoalChange}
        />

        <Select
            label={t('components.CustomizedMixTrancheDetails.period.label')}
            data={periodOptions}
            value={tranche.period || ''}
            error={form.errors.mixes?.[mixIndex]?.tranches?.[trancheIndex]?.period}
            onChange={handlePeriodChange}
        />

        <ActionIcon
            color='red'
            radius='xl'
            bg='#FEF2F2'
            onClick={handleDelete}
        >
          <IconTrash size='1rem' color='#EF4444' />
        </ActionIcon>
      </Flex>

      <Divider />

      <Flex
        bg='white'
        p='md'
        px='xl'
        align='center'
        justify='space-between'
        >

        <Group gap='xl'>
          <Group gap='xs' align='center'>
            <Text c='dimmed' size='sm'>{t('components.CustomizedMixTrancheDetails.totals.totalMonthlyRepayment')}</Text>
            <Text fw={700} size='lg'>{formatNumberWithCommas(monthlyPayment)}</Text>
          </Group>

          <Group gap='xs' align='center'>
            <Text c='dimmed' size='sm'>{t('components.CustomizedMixTrancheDetails.totals.totalRepayment')} </Text>
            <Text span c='dark'>{formatNumberWithCommas(totalRepayment)}</Text>
          </Group>

        </Group>

        <Group>
          <Text c='dimmed' size='sm'>{t('components.CustomizedMixTrancheDetails.totals.linkage')} <Text span c='dark' fw={600}>{tranche.linkage ? `${tranche.linkage}%` : '-'}</Text> </Text>
          <Text c='dimmed' size='sm'>{t('components.CustomizedMixTrancheDetails.totals.interest')} <Text span c='dark' fw={600} >{ tranche.interest ? `${tranche.interest}%` : '-'}</Text></Text>
        </Group>

      </Flex>
    </Container>
  );
};

export default CustomizedMixTrancheDetails;

CustomizedMixTrancheDetails.propTypes = {
  handleDelete: PropTypes.func,
  trancheOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  })),
  loanGoalOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  })),
  periodOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  })),
  form: PropTypes.object,
  mixIndex: PropTypes.number,
  trancheIndex: PropTypes.number,
  tranchesData: PropTypes.object,
};
